/**
 * OnboardingPlanPage — RC 1.32 Plan Selection, RC 1.34.A catalogue-driven.
 *
 * Prices and included-usage values come from the canonical backend
 * catalogue at GET /api/account/plan-catalogue (populated by
 * core/pricing.py). Nothing price-related is hard-coded in this
 * file — a single tweak in core/pricing.py updates every surface.
 *
 * Base → /dashboard. Focus / Flow → /onboarding/payment (stub).
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Zap, Sparkles, Check, Loader2, ShieldCheck, ArrowLeft,
} from 'lucide-react';
import CommercialTrustStrip from '@/components/CommercialTrustStrip';
import LegalFooter from '@/components/LegalFooter';
import OnboardingShell from '@/components/onboarding/OnboardingShell';
import { track } from '@/lib/analytics';
import { useCredits } from '@/lib/creditsContext';
import { getFullyTailoredAppCost } from '@/lib/creditsHelpers';

// RC 1.48.7B (2026-05-04) — the per-app cost divisor was previously
// a hardcoded `FULLY_TAILORED_APP_COST_CREDITS = 25` constant. It
// now comes live from the creditsContext via
// `getFullyTailoredAppCost(usageCosts)` so admin price tunes to
// `credit_action_prices` propagate without a redeploy. The
// price-honesty governance test in
// `backend/tests/test_price_honesty_governance.py` still pins the
// backend costs.

const BILLING_PERIODS = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'annual',  label: 'Annual'  },
];

const PLAN_META = {
  base: {
    icon: ShieldCheck,
    accent: 'border-ndor-edge',
    cta: 'Start free',
    bullets: [
      '2 Analyse Fit per day (free-tier daily limit)',
      'Manual Review — find roles and apply yourself',
      'CV upload, structured parse, application pack composer',
    ],
  },
  focus: {
    icon: Sparkles,
    accent: 'border-ndor-primary/40',
    cta: 'Choose Focus',
    bullets: [
      'Higher monthly Credit allowance',
      'Manual Review + Assisted Review',
      'Assisted Review runs Analyse Fit against your Source CV in the background, up to your daily budget',
      'Matches above your fit threshold appear in your Review Queue',
      'You still pick the CV and submit every application yourself',
    ],
  },
  flow: {
    icon: Zap,
    accent: 'border-ndor-gold/40',
    cta: 'Choose Flow',
    bullets: [
      'Everything in Focus',
      'Manual Review + Assisted Review + background Source CV Baseline Fit',
      'Background Baseline Fit runs against your Source CV continuously — no daily cap, surfaces fresh-fit candidates so you spend Credits where they matter',
      'Strongest matches surface in your Top Opportunities feed, re-ranked throughout the day',
      'NDORFlow never auto-applies and never auto-tailors — you still review, pick the CV, generate any tailored variant, and submit every application yourself',
    ],
  },
};

export default function OnboardingPlanPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(null);
  const [focusPeriod, setFocusPeriod] = useState('monthly');
  const [flowPeriod,  setFlowPeriod]  = useState('monthly');
  // RC 1.48.7B — live per-app cost from creditsContext. Null until
  // the provider bootstraps; the strapline suppresses itself when
  // null so we never render a stale hardcoded number.
  const { usageCosts } = useCredits();
  const perAppCost = getFullyTailoredAppCost(usageCosts);

  // RC 1.34.A — pull the canonical catalogue on mount. Until it
  // resolves we render a skeleton so no stale price briefly appears.
  const [catalogue, setCatalogue] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiFetch('/api/account/plan-catalogue');
        if (alive && Array.isArray(r?.plans)) setCatalogue(r.plans);
      } catch (err) {
        // Surface once; the page stays usable via Base (no price).
        toast.error(err?.message || 'Unable to load plan prices.');
        if (alive) setCatalogue([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  // UI-02 — query the user's current entitlement so we can offer an
  // honest escape hatch. If they already have an active plan, the
  // header back button reads "Back to dashboard" and lands on
  // /dashboard (Base) or /account?tab=plan (paid). If they're a
  // brand-new user with no entitlement, the back button is hidden so
  // they can't bypass plan selection on first sign-in (mirrors the
  // EntitlementGate redirect).
  //
  // RC 1.46.ONBOARDING-BACK (2026-05-02) — `planResolved` split off
  // from `currentPlan` so we can distinguish "API still pending" from
  // "API returned null / errored". The first-run detection below
  // treats BOTH unresolved AND base-without-paid as first-run unless
  // the caller explicitly navigated here from /account.
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planResolved, setPlanResolved] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiFetch('/api/account/plan');
        if (alive) setCurrentPlan(r);
      } catch {
        if (alive) setCurrentPlan(null);
      } finally {
        if (alive) setPlanResolved(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const returnTo =
    location.state?.from
    || (typeof document !== 'undefined' && document.referrer
         && document.referrer.includes('/account')
           ? '/account?tab=plan'
           : null);

  // RC 1.46.ONBOARDING-BACK (2026-05-02) — first-run lock.
  //
  // Operator directive: a brand-new user on /onboarding/plan must
  // NOT be able to slip out to /account, /dashboard, or an
  // arbitrary browser-history destination before plan selection is
  // complete. Back on first-run MUST return to /onboarding (CV
  // upload) only.
  //
  // "First-run" = the user arrived here directly from /onboarding
  // (nav-state marker) OR we have no signal they already manage a
  // plan from Account. Heuristic:
  //
  //   * `location.state.from === '/onboarding'` → definite first-run.
  //     Set explicitly by OnboardingPage when redirecting post-CV.
  //   * Explicit `from` that starts with '/account' → returning
  //     user managing their plan. Preserve existing cascade.
  //   * Otherwise: treat as first-run UNTIL `/api/account/plan`
  //     resolves AND returns a paid (focus/flow) entitlement. This
  //     covers cold deep-links, hard refreshes, and base/no-plan
  //     users — all of whom must be kept inside onboarding.
  //
  // Entitlement rules, pricing, credits, Stripe, and onboarding
  // completion logic are UNCHANGED — this file only narrows the
  // Back button destination.
  const fromOnboarding = location.state?.from === '/onboarding';
  const fromAccount    = typeof location.state?.from === 'string'
    && location.state.from.startsWith('/account');
  const hasPaidPlan    = planResolved
    && currentPlan?.plan_code
    && currentPlan.plan_code !== 'base';
  const isFirstRun     = fromOnboarding || (!fromAccount && !hasPaidPlan);

  // RC 1.35.E.UI — Always show the Back button immediately so there
  // is no invisible window between mount and `/api/account/plan`
  // resolving. Three priorities for the escape target:
  //
  //   1. Explicit `from` route in nav state OR document.referrer
  //      hint (e.g. arrived via "Change plan" or browser back) →
  //      go there. Label "Back".
  //   2. The user has any history entries deeper than this page
  //      (history.length > 1) → safe to use browser back. Targets
  //      whatever the user came from. Label "Back".
  //   3. Cold deeplink with no history and no referrer → fall back
  //      to /dashboard. Label "Back to dashboard".
  //
  // RC 1.46.ONBOARDING-BACK — above cascade only applies when
  // `isFirstRun === false`. First-run users are locked to
  // /onboarding to prevent plan-bypass.
  //
  // currentPlan is an *enrichment*: once it resolves, we may upgrade
  // the destination from /dashboard to /account?tab=plan when the
  // user already holds a paid (active OR pending) plan, since that
  // page is more contextual. We never *remove* the button based on
  // currentPlan — it's always visible.
  const hasHistoryDepth = typeof window !== 'undefined'
    && typeof window.history?.length === 'number'
    && window.history.length > 1;

  const canEscape = true;

  let escapeTarget;
  let escapeLabel;
  if (isFirstRun) {
    // First-run lock: ignore nav-state, history, /account, /dashboard.
    // The only valid way out of plan selection on first run is back
    // to the CV upload step (or explicit sign-out — which is a
    // separate surface, unchanged).
    escapeTarget = '/onboarding';
    escapeLabel  = 'Back to CV upload';
  } else if (returnTo) {
    escapeTarget = returnTo;
    escapeLabel  = 'Back';
  } else if (currentPlan?.plan_code && currentPlan.plan_code !== 'base') {
    // User holds a paid plan (active OR pending) → /account is more
    // contextual than /dashboard for the "go back" semantic.
    escapeTarget = '/account?tab=plan';
    escapeLabel  = 'Back to account';
  } else if (hasHistoryDepth) {
    escapeTarget = '__history_back__';
    escapeLabel  = 'Back';
  } else {
    escapeTarget = '/dashboard';
    escapeLabel  = 'Back to dashboard';
  }

  const handleEscape = () => {
    toast.message('Returning…');
    if (escapeTarget === '__history_back__') {
      navigate(-1);
      return;
    }
    navigate(escapeTarget, { replace: true });
  };

  // Find a plan entry in the catalogue by code.
  const getPlan = (code) => (catalogue || []).find(p => p.plan_code === code);

  const plans = useMemo(() => {
    return ['base', 'focus', 'flow'].map(code => {
      const meta = PLAN_META[code];
      const entry = getPlan(code);
      return {
        code,
        name:     entry?.label || code.charAt(0).toUpperCase() + code.slice(1),
        icon:     meta.icon,
        accent:   meta.accent,
        cta:      meta.cta,
        bullets:  meta.bullets,
        tiers:    entry?.tiers || {},
        period:   code === 'focus' ? focusPeriod : code === 'flow' ? flowPeriod : 'monthly',
        setPeriod:
          code === 'focus' ? setFocusPeriod :
          code === 'flow'  ? setFlowPeriod  : null,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, focusPeriod, flowPeriod]);

  const handleSelect = async (code) => {
    if (submitting) return;
    setSubmitting(code);
    // RC 1.45.PLAN-EVENTS — fire plan_selected the moment the user
    // commits to a tier. Fired BEFORE the API call so we capture the
    // intent even if `/api/account/plan/select` errors out (the
    // signup-survives-Stripe-flap case). `founding` is true for any
    // monthly paid tier while the Founding Member model is live.
    const billingPeriod =
      code === 'focus' ? focusPeriod :
      code === 'flow'  ? flowPeriod  : 'monthly';
    track('plan_selected', {
      plan_code:      code,
      billing_period: billingPeriod,
      founding:       code !== 'base' && billingPeriod === 'monthly',
    });
    try {
      const body = { plan_code: code };
      if (code !== 'base') {
        body.billing_period = billingPeriod;
      }
      const res = await apiFetch('/api/account/plan/select', {
        method: 'POST', body,
      });
      if (res.payment_required) {
        navigate('/onboarding/payment', {
          replace: true,
          state: returnTo ? { from: returnTo } : undefined,
        });
      } else {
        // RC 1.44.O1.B — Base activation routes through the forced
        // re-auth handoff instead of straight to /dashboard, so the
        // user's dashboard paints from a fresh session with the new
        // entitlement already hydrated.
        toast.success('Welcome to Base — you\'re all set.');
        navigate('/plan-activated', { replace: true });
      }
    } catch (err) {
      toast.error(err?.message || 'Could not save your plan selection.');
    } finally {
      setSubmitting(null);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────

  const renderTagline = (plan) => {
    const tier = plan.tiers[plan.period];
    if (plan.code === 'base') return 'Free — pay only if you need more';
    if (!tier) return '—';
    // RC 1.34.B — Annual reads "120 usage per month" (released
    // monthly, not upfront). Monthly stays "120 included usage / month".
    if (plan.period === 'annual') {
      const monthly = plan.tiers.monthly;
      const perMonth = monthly?.included_usage ?? Math.round((tier.included_usage || 0) / 12);
      return `${perMonth.toLocaleString()} Credits per month · released monthly`;
    }
    return `${tier.included_usage.toLocaleString()} included Credits / month`;
  };

  const renderPrice = (plan) => {
    const tier = plan.tiers[plan.period];
    if (plan.code === 'base') return 'Free';
    if (!tier) return '—';
    const suffix = plan.period === 'annual' ? '/yr' : '/mo';
    // RC 1.45.HARMONISE — monthly tiers render the Founding Member
    // price (what Stripe actually charges today) via `early_access_display`.
    // The backend field name is internal API surface — public-facing
    // label is "Founding Member Pricing — locked in for life" (see
    // renderFoundingMemberTag below). Strict rule: NO strikethrough,
    // NO list-price comparison, NO "was £X" copy.
    const founding = tier.early_access_display;
    if (founding && plan.period === 'monthly') {
      return `${founding}${suffix}`;
    }
    return `${tier.display_price}${suffix}`;
  };

  const renderFoundingMemberTag = (plan) => {
    const tier = plan.tiers[plan.period];
    if (!tier?.early_access_display || plan.period !== 'monthly') return null;
    return (
      <>
        <p
          className="text-[11px] uppercase tracking-[0.18em] text-ndor-gold font-mono-ui"
          data-testid={`plan-founding-member-${plan.code}`}
        >
          Founding Member Pricing — locked in for life
        </p>
        <p
          className="text-[11px] text-ndor-ink-muted leading-snug mt-0.5"
          data-testid={`plan-founding-member-context-${plan.code}`}
        >
          Your Founding Member rate is locked in below future pricing.
        </p>
      </>
    );
  };

  // RC 1.45.HARMONISE — outcome strapline mirroring the marketing site
  // copy. "Covers roughly N fully-tailored applications per month" where
  // N = Math.floor(included_usage / 25). For annual plans we use the
  // MONTHLY bundle since Credits are released monthly (RC 1.34.B).
  // Returns null for Base (it's free — no bundle).
  const renderAppCountStrapline = (plan) => {
    if (plan.code === 'base') return null;
    const tier = plan.tiers[plan.period];
    if (!tier) return null;
    const monthlyBundle = plan.period === 'annual'
      ? (plan.tiers.monthly?.included_usage ?? Math.round((tier.included_usage || 0) / 12))
      : tier.included_usage;
    if (!monthlyBundle) return null;
    // RC 1.48.9 pricing-alignment — strapline standardised to a
    // non-numeric phrase per operator directive ("Replace with
    // 'Supports multiple fully tailored applications per month'").
    // The dynamic divisor is intentionally retained as a soft gate
    // so the line is suppressed before usageCosts resolves; this
    // avoids a flash of the strapline when the context is still
    // loading.
    if (!perAppCost) return null;
    return (
      <p
        className="text-xs text-ndor-ink-secondary leading-snug"
        data-testid={`plan-app-count-${plan.code}`}
      >
        Supports multiple fully tailored applications per month.
      </p>
    );
  };

  return (
    <OnboardingShell step={2} totalSteps={2} stepLabel="Choose your plan" testid="onboarding-plan-page">
      <div className="max-w-5xl w-full mx-auto space-y-8">
        {canEscape && (
          <div className="flex pl-3 sm:pl-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEscape}
              data-testid="onboarding-plan-back"
              className="text-ndor-ink-muted hover:text-ndor-ink"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {escapeLabel}
            </Button>
          </div>
        )}

        <header className="text-center space-y-3">
          <h1
            className="text-3xl sm:text-4xl font-serif-display font-semibold tracking-tight text-ndor-ink"
            data-testid="onboarding-plan-title"
          >
            Choose how you want to use NDORFlow
          </h1>
          <p className="text-sm text-ndor-ink-secondary max-w-2xl mx-auto">
            Start free and upgrade any time. Paid plans include a monthly
            Credits allowance and, on Flow, automation. Annual saves more.
          </p>
        </header>

        {catalogue === null ? (
          <div className="flex items-center justify-center py-12 text-sm text-ndor-ink-muted">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading plan prices…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {plans.map(plan => {
              const Icon = plan.icon;
              const isSubmitting = submitting === plan.code;
              return (
                <Card
                  key={plan.code}
                  className={`border-2 ${plan.accent} bg-ndor-surface transition-shadow hover:shadow-md`}
                  data-testid={`plan-card-${plan.code}`}
                >
                  <CardContent className="p-6 flex flex-col h-full gap-4">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-ndor-gold" />
                      <h2 className="text-xl font-serif-display font-semibold text-ndor-ink">
                        {plan.name}
                      </h2>
                    </div>

                    <div
                      className="text-2xl font-serif-display font-semibold text-ndor-ink tabular-nums"
                      data-testid={`plan-price-${plan.code}`}
                    >
                      {renderPrice(plan)}
                    </div>

                    {renderFoundingMemberTag(plan)}

                    <p
                      className="text-sm text-ndor-ink-muted"
                      data-testid={`plan-tagline-${plan.code}`}
                    >
                      {renderTagline(plan)}
                    </p>

                    {renderAppCountStrapline(plan)}

                    <ul className="flex-1 space-y-2">
                      {plan.bullets.map((b, i) => (
                        <li key={i} className="flex gap-2 text-sm text-ndor-ink-secondary">
                          <Check className="h-4 w-4 text-ndor-gold shrink-0 mt-[2px]" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    {plan.code !== 'base' && (
                      <div className="flex items-center gap-1.5 pt-1">
                        {BILLING_PERIODS.map(bp => {
                          const active = plan.period === bp.key;
                          const tier = plan.tiers[bp.key];
                          return (
                            <button
                              key={bp.key}
                              type="button"
                              onClick={() => plan.setPeriod && plan.setPeriod(bp.key)}
                              data-testid={`plan-period-${plan.code}-${bp.key}`}
                              className={`text-xs px-2.5 py-1 rounded-md border transition-colors
                                ${active
                                  ? 'bg-ndor-ink text-ndor-canvas border-ndor-ink'
                                  : 'bg-transparent text-ndor-ink-muted border-ndor-edge hover:bg-ndor-surface-alt/50'}`}
                              title={tier ? `${tier.display_price} · ${tier.included_usage.toLocaleString()} Credits` : ''}
                            >
                              {bp.label}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <Button
                      onClick={() => handleSelect(plan.code)}
                      disabled={Boolean(submitting)}
                      className={`w-full ${plan.code === 'base'
                        ? 'bg-ndor-surface-alt hover:bg-ndor-surface-alt/80 text-ndor-ink border border-ndor-edge'
                        : 'bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground'}`}
                      data-testid={`plan-cta-${plan.code}`}
                    >
                      {isSubmitting
                        ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        : null}
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <p className="text-[11px] text-ndor-ink-muted text-center" data-testid="onboarding-plan-billing-line">
          Secure billing powered by Stripe. Manage your subscription in
          accordance with your plan terms. Cancel any time from Account &gt; Plan.
        </p>

        <CommercialTrustStrip />
      </div>
      <LegalFooter variant="auth" />
    </OnboardingShell>
  );
}
